/**
 * Desktop mega navigation (snippets/ps-mega-nav.liquid).
 *
 * - Hover intent: short delay before the first panel opens, instant swap between panels,
 *   grace period on leave so diagonal mouse paths don't close the menu.
 * - Hovering/focusing a category swaps the product preview on the right.
 * - Keyboard: focus opens, Escape closes and returns focus to the trigger.
 */
const OPEN_DELAY = 90;
const CLOSE_DELAY = 200;

class PsMegaNav extends HTMLElement {
  /** @type {HTMLElement | null} */
  #openItem = null;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    const items = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-nav-item]')]);

    for (const item of items) {
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.#schedule(() => this.#open(item), this.#openItem ? 0 : OPEN_DELAY);
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.#schedule(() => this.#close(), CLOSE_DELAY);
      });
      item.addEventListener('focusin', () => {
        clearTimeout(this.#timer);
        this.#open(item);
      });

      // "Ajutor" is a button, not a link: a click (or Enter after Escape) re-opens it.
      // Focus already opens it, so this never toggles closed — Escape / leaving does that.
      if (item.hasAttribute('data-help')) {
        item.querySelector('[data-trigger]')?.addEventListener('click', () => {
          clearTimeout(this.#timer);
          this.#open(item);
        });
      }

      for (const cat of item.querySelectorAll('[data-preview]')) {
        const show = () => this.#preview(item, /** @type {HTMLElement} */ (cat));
        cat.addEventListener('pointerenter', show);
        cat.addEventListener('focus', show);
      }
    }

    this.addEventListener('focusout', (event) => {
      const next = /** @type {Node | null} */ (event.relatedTarget);
      if (!next || !this.contains(next)) this.#close();
    });

    this.querySelector('[data-scrim]')?.addEventListener('click', () => this.#close());

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#openItem) return;
      const trigger = /** @type {HTMLElement | null} */ (this.#openItem.querySelector('[data-trigger]'));
      this.#close();
      trigger?.focus({ preventScroll: true });
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  /**
   * @param {() => void} fn
   * @param {number} delay
   */
  #schedule(fn, delay) {
    clearTimeout(this.#timer);
    if (delay === 0) fn();
    else this.#timer = window.setTimeout(fn, delay);
  }

  /** @param {HTMLElement} item */
  #open(item) {
    if (this.#openItem === item) return;

    const switching = Boolean(this.#openItem?.querySelector('[data-panel]') && item.querySelector('.ps-nav__panel'));
    if (switching) this.toggleAttribute('data-instant', true);

    this.#close(false);

    const panel = item.querySelector('[data-panel]');
    this.#openItem = item;
    // Only the full-width panels dim the page, not the small "Ajutor" dropdown
    this.toggleAttribute('data-panel-open', Boolean(panel?.classList.contains('ps-nav__panel')));
    if (!panel) return;

    this.#hydrate(panel);
    this.#warmImages(panel);
    item.classList.add('is-open');
    item.querySelector('[data-trigger]')?.setAttribute('aria-expanded', 'true');

    if (switching) requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute('data-instant')));
  }

  #close(clearState = true) {
    const item = this.#openItem;
    if (item) {
      item.classList.remove('is-open');
      item.querySelector('[data-trigger]')?.setAttribute('aria-expanded', 'false');
    }
    if (clearState) {
      this.#openItem = null;
      this.removeAttribute('data-panel-open');
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} cat
   */
  #preview(item, cat) {
    const index = cat.dataset.preview;
    for (const el of item.querySelectorAll('[data-preview]')) {
      el.classList.toggle('is-active', el === cat);
    }
    for (const set of item.querySelectorAll('[data-set]')) {
      set.classList.toggle('is-active', /** @type {HTMLElement} */ (set).dataset.set === index);
    }
  }

  /**
   * Product previews ship inside a <template> so phones never build them; stamp them in on first open.
   * @param {Element} panel
   */
  #hydrate(panel) {
    const template = panel.querySelector('template[data-preview-template]');
    if (template instanceof HTMLTemplateElement) template.replaceWith(template.content);
  }

  /**
   * Panel images are lazy and hidden until hover; start them all loading as soon as the panel opens
   * so swapping categories doesn't flash empty tiles.
   * @param {Element} panel
   */
  #warmImages(panel) {
    if (panel.hasAttribute('data-warm')) return;
    panel.setAttribute('data-warm', '');
    for (const img of panel.querySelectorAll('img[loading="lazy"]')) {
      /** @type {HTMLImageElement} */ (img).loading = 'eager';
    }
  }
}

if (!customElements.get('ps-mega-nav')) {
  customElements.define('ps-mega-nav', PsMegaNav);
}
